/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path ui
 * @description Breadcrumbs and the home button
 * @section 4.6
 * @section 4.8
 * @section 4.2.1.2
 */
import { Typography } from "@material-ui/core";
import MuiBreadcrumbs from "@material-ui/core/Breadcrumbs";
import Fade from "@material-ui/core/Fade";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { t } from "i18next";
import React from "react";
import { Link, useParams } from "react-router-dom";
import useParents, { MEAPathElement } from "./../apihooks/useParents";
import { useAppContext } from "./../context/AppCtxProvider";
import useCreateLink from "./../hooks/useCreateLink";
import { useActiveModuleRoute } from "./../modules/moduleRegistry";
import { mea_t } from "./../tplutils/utils";
import typenameToViewMap from "./../typenames/typenameToViewMap";
import { useIdBySymbol } from "./../utils/model";
import { makeThemedStyles } from "./../viewcomponents/utils";
import useQueryParams from "./hooks/useQueryParams";
const useMuiBreadcrumbsStyles = makeThemedStyles("Breadcrumbs", (theme) => ({
  root: {
    width: "calc(100% - 40px)",
  },
  ol: {
    flexWrap: "unset",
  },
  li: {
    overflow: "hidden",
    minWidth: "1.9em",
    direction: "initial",
  },
  separator: {
    color: "rgba(255, 255, 255, 0.5)",
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));
const useBreadcrumbSeparatorIconStyles = makeThemedStyles(
  "Breadcrumbs.SeparatorIcon",
  (theme) => ({
    root: {
      width: "0.5em",
      height: "0.5em",
    },
    reverse: {
      transform: "rotate(180deg)",
    },
  })
);
const useBreadcrumbsHomeIconStyles = makeThemedStyles(
  "Breadcrumbs.HomeIcon",
  (theme) => ({
    root: {
      width: "20px",
      height: "20px",
      color: "rgba(255, 255, 255, 0.5)",
      "&:hover": { color: "rgba(255, 255, 255, 0.8)" },
    },
  })
);
const useBreadcrumbStyles = makeThemedStyles("Breadcrumbs.Item", (theme) => ({
  link: {
    textDecoration: "none",
  },
  text: {
    textOverflow: "ellipsis",
    fontSize: "14px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "5vw",
    color: "rgba(255, 255, 255, 0.5)",
    "&:hover": { color: "rgba(255, 255, 255, 0.8)" },
  },
}));
const useLastBreadcrumbStyles = makeThemedStyles(
  "Breadcrumbs.LastItem",
  (theme) => ({
    text: {
      color: "rgba(255, 255, 255, 0.9)",
      fontSize: "14px",
      textDecoration: "none",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  })
);
const useToolTipStyle = makeThemedStyles("", () => ({
  popper: {
    zIndex: 99999999,
  },
  tooltip: {
    lineHeight: "18px",
  },
}));
type BreadcrumbWithID = {
  id: string;
  name: string;
  typename: string;
  JS_ATTR?: any;
  parentId: string;
};
type BreadcrumbWithTitleAndURL = {
  url: string;
  title: string;
};
type Breadcrumb = BreadcrumbWithID | BreadcrumbWithTitleAndURL;
function isBreadcrumbWithID(item: Breadcrumb): item is BreadcrumbWithID {
  return typeof (item as unknown as any).id !== "undefined";
}
function isBreadcrumbWithTitle(
  item: Breadcrumb
): item is BreadcrumbWithTitleAndURL {
  return typeof (item as unknown as any).title !== "undefined";
}
const HomeBreadcrumb = () => {
  const { tenantSymbol } = useParams<{
    tenantSymbol: string;
  }>();
  const classes: any = useBreadcrumbsHomeIconStyles();
  const { HomeIcon, ...other } = classes;
  const model = useAppContext((s) => s.model);
  return (
    <Link to={`/${tenantSymbol}/${model}/landingpage`}>
      <HomeIcon classes={other} />
    </Link>
  );
};
const MuiToolTip = ({
  title,
  children,
}: {
  title: string;
  children: JSX.Element;
}) => {
  const classes = useToolTipStyle();
  return (
    <Tooltip
      placement="bottom"
      classes={{
        popper: classes.popper,
        tooltip: classes.tooltip,
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 0 }}
      title={<h1>{t(title)}</h1>}
    >
      {children}
    </Tooltip>
  );
};
const BreadcrumbComponent = (props: Breadcrumb) => {
  const classes = useBreadcrumbStyles();
  const lang = useAppContext((state) => state.language);
  const createLink = useCreateLink();
  if (isBreadcrumbWithTitle(props)) {
    return (
      <Link to={props.url} className={classes.link}>
        <MuiToolTip title={props.title}>
          <Typography className={classes.text}>{t(props.title)}</Typography>
        </MuiToolTip>
      </Link>
    );
  }
  return (
    <Link
      to={createLink({
        page: typenameToViewMap[props.typename],
        meaId: props.id,
      })}
      className={classes.link}
    >
      <MuiToolTip title={mea_t("Name", props.JS_ATTR, lang)}>
        <Typography className={classes.text}>
          {props.JS_ATTR ? mea_t("Name", props.JS_ATTR, lang) : t(props.name)}
        </Typography>
      </MuiToolTip>
    </Link>
  );
};
const LastBreadCrumb = (props: Breadcrumb) => {
  const classes = useLastBreadcrumbStyles();
  const lang = useAppContext((state) => state.language);
  if (isBreadcrumbWithTitle(props)) {
    return (
      <Link to={props.url} className={classes.text}>
        <MuiToolTip title={props.title}>
          <Typography className={classes.text}>{t(props.title)}</Typography>
        </MuiToolTip>
      </Link>
    );
  }
  return (
    <MuiToolTip title={mea_t("Name", props.JS_ATTR, lang)}>
      <Typography className={classes.text}>
        {props.JS_ATTR ? mea_t("Name", props.JS_ATTR, lang) : t(props.name)}
      </Typography>
    </MuiToolTip>
  );
};
const useBreadcrumb = (): Array<Breadcrumb> => {
  const { meaId, id } = useParams<{
    meaId: string;
    id: string;
  }>();
  const activePage = useAppContext((state) => state.page);
  const lang = useAppContext((state) => state.language);
  const data = useParents(meaId, false);
  const model = useAppContext((state) => state.model);
  const link = useCreateLink();
  const queryParms = useQueryParams();
  const { data: sectorMain, isLoading: isSectorLoading } = useIdBySymbol(
    model,
    "SECTOR_MAIN"
  );
  const { data: geMain, isLoading: isGeLoading } = useIdBySymbol(
    model,
    "GE_MAIN"
  );
  if (!data) return [];
  const addPathElement = (list: Breadcrumb[], item: MEAPathElement) => {
    if (item.typename === "KPI") {
      list.push({
        url: link({ page: "kpidetails", meaId: item.id }),
        title: mea_t("Name", item.JS_ATTR, lang),
      });
    }
    if (item.typename === "Project") {
      list.push({
        url: link({ page: "project", meaId: item.id }),
        title: mea_t("Name", item.JS_ATTR, lang),
      });
    }
    if (item.name === "Sectors") {
      list.push({
        url: link({
          page: "sectors",
          meaId: sectorMain?.data[0]?.topelementid,
        }),
        title: model === "DP2030" ? t("Clusters") : t("Sectors"),
      });
      return;
    }
    if (item.typename === "Sector") {
      list.push({
        url: link({ page: "sector", meaId: item.id }),
        title: mea_t("Name", item.JS_ATTR, lang),
      });
    }
    if (item.typename === "Sub-Sector") {
      list.push({
        url: link({ page: "subsector", meaId: item.id }),
        title: mea_t("Name", item.JS_ATTR, lang),
      });
    }
    if (item.typename === "Government Entity") {
      list.push({
        url: link({
          page: "governmententities",
          meaId: geMain?.data[0]?.topelementid,
        }),
        title: t("Government Entities"),
      });
      if (activePage !== "governmententities") {
        list.push({
          url: link({ page: "governmententity", meaId: item.id }),
          title: mea_t("Name", item.JS_ATTR, lang),
        });
      }
    }
  };
  const WalkList = () => {
    const list: Breadcrumb[] = [];
    data.forEach((item) => {
      addPathElement(list, item);
    });
    return list;
  };
  /**
   * Symbol "TOP_PLAN is here for Plan level"
   */
  if (
    data?.[0]?.typename === "Sectors" ||
    (data?.[0]?.typename === "Government Entity" &&
      data?.[0]?.symbol !== "TOP_PLAN" &&
      activePage !== "themes" &&
      activePage !== "governmententities")
  ) {
    return WalkList();
  }
  const params = new URLSearchParams(window.location.search);
  switch (activePage) {
    case "usersform":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/usersmanagement/`,
          title: t("Manage users and groups"),
        },
        {
          url: `/tec/${model}/usersform/${meaId}`,
          title: `${meaId ? t("Edit user") : t("Add user")}`,
        },
      ];
    case "groupsform":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/usersmanagement/`,
          title: t("Manage users and groups"),
        },
        {
          url: `/tec/${model}/groupsform/${meaId}`,
          title: meaId ? t("Edit Group") : t("Create Group"),
        },
      ];
    case "groupsmanagement":
    case "usersmanagement":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/usersmanagement/`,
          title: t("Manage users and groups"),
        },
      ];
    case "kpisform":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/kpisandprojectsmanagement/`,
          title: t("KPIs and Projects Management"),
        },
        {
          url: `/tec/${model}/kpisform/`,
          title:
            queryParms.get("type") === "existing"
              ? t("Edit KPI")
              : queryParms.get("type") === "delete"
              ? t("Delete KPI")
              : t("Add New KPI"),
        },
      ];
    case "projectsform":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/kpisandprojectsmanagement/`,
          title: t("KPIs and Projects Management"),
        },
        {
          url: `/tec/${model}/projectsform/`,
          title:
            queryParms.get("type") === "existing"
              ? t("Edit Project")
              : queryParms.get("type") === "delete"
              ? t("Delete Project")
              : t("Add New Project"),
        },
      ];
    case "kpisandprojectsmanagement":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
        {
          url: `/tec/${model}/kpisandprojectsmanagement/`,
          title: t("KPIs and Projects Management"),
        },
      ];
    case "admintools":
      return [
        {
          url: `/tec/${model}/admintools/`,
          title: t("Admin tools"),
        },
      ];
    case "register":
      return [
        {
          url: `/tec/${model}/register/`,
          title: t("Mobile Application Registration"),
        },
      ];
    case "operationaldashboard":
      return [
        {
          url: `/tec/${model}/operationaldashboard/`,
          title: t("Operational dashboard"),
        },
      ];
    case "themes":
      return [
        {
          url: `/tec/${model}/themes/${meaId}/`,
          title:
            model === "DP2030" ? t("The Dubai Plan") : t("Dubai Plan 2021"),
        },
      ];
    case "governmententities":
      return [
        {
          url: `/tec/${model}/governmententities/${meaId}/`,
          title: t("Government Entities"),
        },
      ];
    case "governmententitykpisgrid":
    case "governmententitykpistable":
    case "governmententityprojectsgrid":
    case "governmententityprojectstable":
    case "governmententity":
      return [
        {
          url: `/tec/${model}/governmententities`,
          title: "Government Entities",
        },
        {
          url: `/tec/${model}/governmententity/${meaId}`,
          title: data
            ? mea_t("Name", data[data?.length - 1].JS_ATTR, lang)
            : "unknown",
        },
      ];
    case "sectorprojectstable":
    case "sectorprojectsgrid":
    case "sectorkpistable":
    case "sectorkpisgrid":
    case "sector":
      return [
        {
          url: `/tec/${model}/sectors`,
          title: model === "DP2030" ? "Clusters" : "Sectors",
        },
        {
          url: `/tec/${model}/sector/${meaId}`,
          title: data
            ? mea_t("Name", data[data?.length - 1].JS_ATTR, lang)
            : "unknown",
        },
      ];
    case "subsectorkpistable":
    case "subsectorkpisgrid":
    case "subsectorprojectslist":
    case "subsectorprojectsgrid":
    case "subsector":
      return [
        {
          url: `/tec/${model}/sectors`,
          title: model === "DP2030" ? "Clusters" : "Sectors",
        },
        {
          url: `/tec/${model}/sector/${
            data ? data[data?.length - 2]?.id : undefined
          }`,
          title: data
            ? mea_t("Name", data[data?.length - 2].JS_ATTR, lang)
            : "unknown",
        },
        {
          url: `/tec/${model}/subsector/${meaId}`,
          title: data
            ? mea_t("Name", data[data?.length - 1].JS_ATTR, lang)
            : "unknown",
        },
      ];
    case "sectors":
      return [
        {
          url: `/tec/${model}/sectors`,
          title: model === "DP2030" ? "Clusters" : "Sectors",
        },
      ];
    case "executivedashboard":
      return [
        {
          url: `/tec/${model}/executivedashboard`,
          title: "Executive Dashboard",
        },
      ];
    case "mytasks":
      return [
        {
          url: `/tec/${model}/mytasks`,
          title: "My Tasks",
        },
      ];
    case "mycompletedtasks":
      return [
        {
          url: `/tec/${model}/mycompletedtasks`,
          title: "My Completed Task",
        },
      ];
    case "reports":
      return [
        {
          url: `/tec/${model}/reports`,
          title: "Reports",
        },
      ];
    case "searchresults":
      return [
        {
          url: `/tec/${model}/searchresults`,
          title: "Search",
        },
      ];
    case "kpisapproval":
      return [
        {
          url: `/tec/${model}/kpisapproval`,
          title: "Kpi Approval",
        },
        {
          url: `/tec/${model}/kpisapproval/${meaId}`,
          title: data
            ? mea_t("Name", data[data?.length - 1].JS_ATTR, lang)
            : "unknown",
        },
      ];
    case "acceptmilestone":
      return [
        {
          url: `/tec/${model}/acceptmilestone/${meaId}`,
          title: t("Project approval"),
        },
        {
          url: `/tec/${model}/acceptmilestone/${meaId}`,
          title: data
            ? mea_t("Name", data[data?.length - 1].JS_ATTR, lang)
            : "unknown",
        },
      ];
    default:
  }
  return data || [];
};
const Breadcrumbs = (props: { useBreadcrumb: () => Array<Breadcrumb> }) => {
  const { meaId } = useParams<{
    meaId: string;
  }>();
  const breadcrumbClasses = useMuiBreadcrumbsStyles();
  const separatorClasses = useBreadcrumbSeparatorIconStyles();
  // const data = useParents(meaId);
  const data = props.useBreadcrumb();
  const rtl = useAppContext((state) => state.rtl);
  console.log("useParents BreadCrumb data ", data);
  if (!data || data.length === 0) return null;
  console.log("useParents BreadCrumb data ", data);
  const last = data.pop();
  return (
    <MuiBreadcrumbs
      separator={
        <ArrowForwardIosIcon
          className={`${separatorClasses.root} ${
            rtl ? separatorClasses.reverse : ""
          }`}
        />
      }
      classes={breadcrumbClasses}
    >
      <HomeBreadcrumb />
      {data.length &&
        data.map((item: Breadcrumb) => (
          <BreadcrumbComponent key={JSON.stringify(item)} {...item} />
        ))}
      {last && <LastBreadCrumb {...last} />}
    </MuiBreadcrumbs>
  );
};
/**
 * Stores unique key value for individual objects using Map to minimize re-renders
 * caused by the hook change.
 */
const getMapKey = (() => {
  // cache for creating unique key values
  let mapKey = 0;
  const activeMap = new Map<any, string>();
  return (item: any) => {
    if (activeMap.has(item)) {
      return activeMap.get(item);
    }
    mapKey += 1;
    const key = `key${mapKey}`;
    activeMap.set(item, key);
    return key;
  };
})();
const BreadcrumbsMain = () => {
  const moduleRoute = useActiveModuleRoute();
  const hook =
    moduleRoute && moduleRoute.useBreadcrumb
      ? moduleRoute?.useBreadcrumb
      : useBreadcrumb;
  /**
   * The key must be used here to force unmount of the component to clear the hook instance cache
   * based on the instance of the hook, thus each hook has unique key
   */
  return <Breadcrumbs useBreadcrumb={hook} key={getMapKey(hook)} />;
};
export default BreadcrumbsMain;
